import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Button from '../../components/button';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import AttachmentViewerModal from '../../components/modals/AttachmentViewerModal';
import Select from '../../components/Select';
import {
  createTripExpense,
  deleteTripExpense,
  expenseCategoryDropdown,
  getTripDetails,
  updateTripExpense
} from '../../store/Slices/TripSlice';
import { formatDate } from '../../utils/helper';
import { tripExpenseSchema } from '../../utils/Schema';
import { errorNotify, successNotify } from '../../utils/toast';
import { FiEdit, FiEye } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';

const getAvailableAmountError = (amount, availableAmount) => {
  if (amount === '' || amount === null || amount === undefined) return '';

  const enteredAmount = Number(amount);
  if (!Number.isFinite(enteredAmount)) return '';

  if (enteredAmount > Number(availableAmount || 0)) {
    return `Amount cannot exceed available PKR ${Number(availableAmount || 0).toLocaleString()}`;
  }

  return '';
};

const mapUploadedFilesForPayload = (files = []) =>
  (Array.isArray(files) ? files : [])
    .map((file) => {
      if (typeof file === 'string') return file;
      return file?.fileName || file?.file_path || file?.file_name || '';
    })
    .filter(Boolean);

const TripExpenses = ({
  tripExpenses,
  vehicle_id,
  driver_id,
  isTripCompleted = false,
  totalPettyCashAmount = 0,
  totalTripExpenseAmount = 0,
  remainingPettyCashAmount = 0,
  canManageTripExpenses = false,
  tripExpensesLockReason = '',
  canAddTripExpense = true,
  canEditTripExpense = true,
  canDeleteTripExpense = true,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [showAddItemBtn, setShowAddItemBtn] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [attachmentPreviewFiles, setAttachmentPreviewFiles] = useState([]);

  const {
    expenseCategoryDropdownData,
    createTripExpenseLoading,
    updateTripExpenseLoading
  } = useSelector((state) => state.trips);

  const expenseCategoryDropdownOptions = expenseCategoryDropdownData?.map((item) => ({
    value: item.slug,
    label: item.title
  })) || [];

  useEffect(() => {
    dispatch(expenseCategoryDropdown());
  }, [dispatch]);

  const initialValues = {
    amount: editData ? editData.amount : '',
    trip_expense_category: editData ? editData.trip_expense_category : '',
  };

  const currentEditableAmount = useMemo(() => {
    return Number(editData?.amount || 0);
  }, [editData]);

  const availableAmountForCurrentAction = useMemo(() => {
    if (isEditing) {
      return Math.max(Number(remainingPettyCashAmount || 0) + currentEditableAmount, 0);
    }
    return Math.max(Number(remainingPettyCashAmount || 0), 0);
  }, [isEditing, remainingPettyCashAmount, currentEditableAmount]);

  const handlePettyCashSubmit = async (values, { setSubmitting, resetForm, setFieldError, setFieldTouched }) => {
    try {
      if (isEditing ? !canEditTripExpense : !canAddTripExpense) {
        errorNotify(`You do not have permission to ${isEditing ? 'edit' : 'add'} trip expenses.`);
        setSubmitting(false);
        return;
      }

      const enteredAmount = Number(values.amount || 0);

      if (!enteredAmount || enteredAmount <= 0) {
        errorNotify('Amount must be greater than 0');
        setSubmitting(false);
        return;
      }

      if (totalPettyCashAmount <= 0) {
        errorNotify('Trip Expenses can only be added when petty cash exists.');
        setSubmitting(false);
        return;
      }

      if (enteredAmount > availableAmountForCurrentAction) {
        setFieldTouched('amount', true, false);
        setFieldError('amount', getAvailableAmountError(values.amount, availableAmountForCurrentAction));
        setSubmitting(false);
        return;
      }

      const payload = {
        vehicle_id: vehicle_id,
        trip_id: parseInt(id),
        amount: parseFloat(values.amount),
        trip_expense_category: values.trip_expense_category,
        driver_id: driver_id,
        files: mapUploadedFilesForPayload(uploadedFiles)
      };

      if (isEditing) {
        payload.trip_expense_id = editId;
        await dispatch(updateTripExpense(payload)).unwrap();
        successNotify('Trip expense updated successfully');
      } else {
        await dispatch(createTripExpense(payload)).unwrap();
        successNotify('Trip expense added successfully');
      }

      dispatch(getTripDetails(id));

      resetForm();
      setShowAddItemBtn(false);
      setUploadedFiles([]);
      setIsEditing(false);
      setEditId(null);
      setEditData(null);
    } catch (error) {
      errorNotify(error?.message || `Failed to ${isEditing ? 'update' : 'add'} trip expense`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    if (!canManageTripExpenses || !canEditTripExpense) return;

    setEditData({
      amount: item.amount,
      trip_expense_category: item.trip_expense_category
    });
    setEditId(item.id);
    setIsEditing(true);
    setShowAddItemBtn(true);
    setUploadedFiles(Array.isArray(item?.files) ? item.files : []);
  };

  const handleDelete = async (itemId) => {
    if (!canManageTripExpenses || !canDeleteTripExpense) return;

    try {
      await dispatch(deleteTripExpense(itemId)).unwrap();
      successNotify('Trip expense deleted successfully');
      dispatch(getTripDetails(id));
    } catch (error) {
      errorNotify(error?.message || "Failed to delete trip expense");
    }
  };

  const handleCancel = () => {
    setShowAddItemBtn(false);
    setIsEditing(false);
    setEditId(null);
    setEditData(null);
    setUploadedFiles([]);
  };

  const openAttachmentViewer = (files = []) => {
    const nextFiles = Array.isArray(files) ? files : [];
    if (!nextFiles.length) {
      errorNotify('No attachments found');
      return;
    }

    setAttachmentPreviewFiles(nextFiles);
  };

  return (
    <div className='service_line_items_main' style={{ border: "none", padding: "0px" }}>
      <div className='service_line_items_table' style={{ marginBottom: "20px" }}>
        <Table responsive>
          <thead>
            <tr>
              <th>Driver</th>
              <th>Expense Category</th>
              <th>Amount (PKR)</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {
              tripExpenses?.length > 0 ? (
                tripExpenses.map((item, index) => (
                  <tr key={index}>
                    <td>{item?.driver?.username || '-'}</td>
                    <td>{item.expense_category?.title || '-'}</td>
                    <td>{item.amount || '0'}</td>
                    <td>{formatDate(item.created_at)}</td>
                    <td>
                      <div className='d-flex gap-2 justify-content-center'>
                        <FiEdit
                          style={{
                            cursor: canManageTripExpenses ? "pointer" : "not-allowed",
                            color: canManageTripExpenses ? "#4CAF50" : "#BDBDBD"
                          }}
                          onClick={() => handleEdit(item)}
                        />
                        <RiDeleteBinLine
                          style={{
                            cursor: canManageTripExpenses ? "pointer" : "not-allowed",
                            color: canManageTripExpenses ? "#F44336" : "#BDBDBD"
                          }}
                          onClick={() => handleDelete(item.id)}
                        />
                        {item?.files && item?.files?.length > 0 && (
                          <button
                            type="button"
                            className="trip_attachment_view_btn"
                            onClick={() => openAttachmentViewer(item.files)}
                            title="View attachments"
                          >
                            <FiEye /> {item.files.length}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center" style={{ padding: "20px", color: "#888" }}>
                    No trip expenses found
                  </td>
                </tr>
              )
            }
          </tbody>
        </Table>
      </div>

      {!showAddItemBtn && (
        <>
          <button
            className='add_issue_btn'
            type="button"
            disabled={!canManageTripExpenses}
            onClick={() => {
              if (!canManageTripExpenses) return;
              setEditData(null);
              setIsEditing(false);
              setShowAddItemBtn(true);
              setUploadedFiles([]);
            }}
          >
            + Add Trip Expense
          </button>

          {!canManageTripExpenses && tripExpensesLockReason && (
            <p style={{ marginTop: "10px", color: "#D32F2F", fontSize: "13px" }}>
              {tripExpensesLockReason}
            </p>
          )}

          {canManageTripExpenses && (
            <p style={{ marginTop: "10px", color: "#2E7D32", fontSize: "13px" }}>
              Available petty cash for Trip Expenses: PKR {availableAmountForCurrentAction.toLocaleString()}
            </p>
          )}
        </>
      )}

      <div className='add_petty_cash'>
        {
          showAddItemBtn &&
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={tripExpenseSchema}
            validate={(values) => {
              const validationErrors = {};
              const amountLimitError = getAvailableAmountError(
                values.amount,
                availableAmountForCurrentAction
              );

              if (amountLimitError) {
                validationErrors.amount = amountLimitError;
              }

              return validationErrors;
            }}
            onSubmit={handlePettyCashSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, submitCount }) => {
              const amountLimitError = getAvailableAmountError(
                values.amount,
                availableAmountForCurrentAction
              );
              const amountError = amountLimitError || errors.amount;
              const showAmountError = Boolean(touched.amount || submitCount > 0 || amountLimitError);

              return (
                <div>
                <h4>{isEditing ? 'Edit Trip Expense' : 'Add Trip Expense'}</h4>
                <Row>
                  <Col md={6}>
                    <Input
                      label="Amount (PKR)"
                      type="number"
                      placeholder="00"
                      name="amount"
                      value={values.amount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={amountError}
                      touched={showAmountError}
                    />
                    <small style={{ color: "#2E7D32" }}>
                      Available: PKR {availableAmountForCurrentAction.toLocaleString()}
                    </small>
                  </Col>

                  <Col md={6}>
                    <Select
                      label="Trip Expense Category"
                      placeholder="Select Trip Expense Category"
                      options={expenseCategoryDropdownOptions}
                      name="trip_expense_category"
                      value={expenseCategoryDropdownOptions.find(
                        (option) => option.value === values.trip_expense_category
                      )}
                      onChange={(opt) => setFieldValue("trip_expense_category", opt ? opt.value : "")}
                      onBlur={handleBlur}
                      error={errors.trip_expense_category}
                      touched={touched.trip_expense_category}
                      isSearch={false}
                    />
                  </Col>

                  <Col md={12}>
                    <div className="photo_document_section" style={{ border: "none", padding: "0", marginTop: "0" }}>
                      <FileUpload
                        destination="fuel-logs"
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                        maxFileSize={10}
                        tripId={id}
                      />
                    </div>
                  </Col>

                  <Col md={12} className="mt-3">
                    <div className='d-flex gap-3'>
                      <Button
                        cancelBtn={handleCancel}
                        loading={createTripExpenseLoading || updateTripExpenseLoading}
                        disabled={createTripExpenseLoading || updateTripExpenseLoading || Boolean(amountLimitError)}
                        submitBtn={handleSubmit}
                        submitText={isEditing ? "Update" : "Save"}
                        shouldRow={false}
                        buttonExtraCss="w-100"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              );
            }}
          </Formik>
        }
      </div>
      <AttachmentViewerModal
        show={attachmentPreviewFiles.length > 0}
        files={attachmentPreviewFiles}
        heading="Trip Expense Attachments"
        handleClose={() => setAttachmentPreviewFiles([])}
      />
    </div>
  );
};

export default TripExpenses;
